import { useEffect } from "react";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { updateAccount } from "@/actions/commonActions";
import { selectAccountById } from "@/entities/accounts/table";
import { getUser } from "selectors/commonSelectors";
import { ReduxRootState } from "@/types/redux-store";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import {
  BestTimeSlotApi,
  clearAccountQueue,
  getQueueSetting,
  updateQueueSettings,
} from "api/account-queue";
import {
  formatTimeSlots,
  getTimeSlotsByDays,
  parseTimeSlots,
} from "../lib/time-slots";

type QueueSchedules = Record<number, number[]>;

export type AccountQueueManagingState = {
  accountId: string;
  enabled: boolean;
  persistedEnabled: boolean;
  persistedSchedules: QueueSchedules;
  schedules: QueueSchedules;
  notifyEmptyQueue: boolean;
  persistedNotifyEmptyQueue: boolean;
  bestTimeSlots: BestTimeSlotApi[];
  timeZone: string;
  persistedTimeZone: string;
  showBestTime: boolean;
  loading: boolean;
  saving: boolean;
  clearing: boolean;
};

const featureKey = "accountQueueManaging" as const;

const initialState: AccountQueueManagingState = {
  accountId: "",
  enabled: false,
  persistedEnabled: false,
  persistedSchedules: {},
  schedules: {},
  notifyEmptyQueue: false,
  persistedNotifyEmptyQueue: false,
  bestTimeSlots: [],
  timeZone: "UTC",
  persistedTimeZone: "UTC",
  showBestTime: true,
  loading: false,
  saving: false,
  clearing: false,
};

function getFeatureState(state: ReduxRootState): AccountQueueManagingState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: AccountQueueManagingState;
      }
    )[featureKey] ?? initialState
  );
}

function getDefaultTimezone(state: ReduxRootState): string {
  const currentUser = getUser(state);

  if (currentUser?.timezone) {
    return currentUser.timezone;
  }

  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (browserTimezone) {
      return browserTimezone;
    }
  } catch {
    // Ignore environments that do not support timezone introspection.
  }

  return "UTC";
}

function shouldPersistQueueSettings(state: AccountQueueManagingState) {
  return Object.keys(state.schedules).length > 0 || !state.enabled;
}

export const openAccountQueueSettings = createAsyncThunk(
  "accountQueueManaging/open",
  async (
    input: string | { accountId: string; legacyAutoSave?: boolean },
    thunkApi
  ) => {
    const accountId = typeof input === "string" ? input : input.accountId;
    const state = thunkApi.getState() as ReduxRootState;
    const account = selectAccountById(state, accountId);

    thunkApi.dispatch(
      accountQueueManagingActions.scopeOpened({
        accountId,
        enabled: Boolean(account?.queueEnabled),
        timeZone: getDefaultTimezone(state),
      })
    );

    try {
      const settings = await getQueueSetting(accountId);

      return { accountId, settings };
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const saveAccountQueueSettings = createAsyncThunk(
  "accountQueueManaging/save",
  async (
    options:
      | {
          accountId?: string;
          silent?: boolean;
          legacyAutoSave?: boolean;
        }
      | undefined,
    thunkApi
  ) => {
    const state = thunkApi.getState() as ReduxRootState;
    const queueState = getFeatureState(state);
    const targetAccountId = options?.accountId ?? queueState.accountId;

    if (!targetAccountId) {
      throw new Error("Account queue settings are not scoped to an account");
    }

    const payload = {
      enabled: queueState.enabled,
      notifyEmptyQueue: queueState.notifyEmptyQueue,
      schedules: formatTimeSlots(queueState.schedules),
      timezone: queueState.timeZone,
    };

    try {
      await updateQueueSettings(targetAccountId, payload);
      thunkApi.dispatch(
        updateAccount(targetAccountId, { queueEnabled: payload.enabled })
      );
      if (!options?.silent) {
        toast.success(
          payload.enabled
            ? "Successfully updated your queue settings."
            : "Successfully disabled your queue."
        );
      }

      return { accountId: targetAccountId, payload };
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

async function persistQueueSettingsIfRequested(
  persist: boolean | undefined,
  thunkApi: {
    dispatch: (action: unknown) => Promise<unknown>;
    getState: () => unknown;
  }
) {
  if (!persist) return;

  const queueState = getFeatureState(thunkApi.getState() as ReduxRootState);
  if (!shouldPersistQueueSettings(queueState)) return;

  await thunkApi.dispatch(
    saveAccountQueueSettings({ legacyAutoSave: true }) as never
  );
}

export const toggleQueueEnabled = createAsyncThunk(
  "accountQueueManaging/toggleQueueEnabled",
  async (options: { persist?: boolean } | undefined, thunkApi) => {
    thunkApi.dispatch(accountQueueManagingActions.queueEnableToggled());
    await persistQueueSettingsIfRequested(options?.persist, thunkApi);
  }
);

export const toggleNotifyEmptyQueue = createAsyncThunk(
  "accountQueueManaging/toggleNotifyEmptyQueue",
  async (options: { persist?: boolean } | undefined, thunkApi) => {
    thunkApi.dispatch(accountQueueManagingActions.notifyEmptyQueueToggled());
    await persistQueueSettingsIfRequested(options?.persist, thunkApi);
  }
);

export const addQueueTimeSlot = createAsyncThunk(
  "accountQueueManaging/addTimeSlot",
  async (
    { day, value, persist }: { day: number; value: number; persist?: boolean },
    thunkApi
  ) => {
    thunkApi.dispatch(
      accountQueueManagingActions.timeSlotAdded({ day, value })
    );
    await persistQueueSettingsIfRequested(persist, thunkApi);
  }
);

export const editQueueTimeSlot = createAsyncThunk(
  "accountQueueManaging/editTimeSlot",
  async (
    {
      day,
      oldValue,
      value,
      persist,
    }: {
      day: number;
      oldValue: number;
      value: number;
      persist?: boolean;
    },
    thunkApi
  ) => {
    thunkApi.dispatch(
      accountQueueManagingActions.timeSlotEdited({ day, oldValue, value })
    );
    await persistQueueSettingsIfRequested(persist, thunkApi);
  }
);

export const deleteQueueTimeSlot = createAsyncThunk(
  "accountQueueManaging/deleteTimeSlot",
  async (
    { day, value, persist }: { day: number; value: number; persist?: boolean },
    thunkApi
  ) => {
    thunkApi.dispatch(
      accountQueueManagingActions.timeSlotDeleted({ day, value })
    );
    await persistQueueSettingsIfRequested(persist, thunkApi);
  }
);

export const clearAccountQueueFx = createAsyncThunk(
  "accountQueueManaging/clear",
  async (_: { legacyAutoSave?: boolean } | undefined, thunkApi) => {
    const state = thunkApi.getState() as ReduxRootState;
    const { accountId } = getFeatureState(state);

    if (!accountId) {
      throw new Error("Account queue settings are not scoped to an account");
    }

    await clearAccountQueue(accountId);
    thunkApi.dispatch(updateAccount(accountId, { queueEnabled: false }));

    return accountId;
  }
);

const accountQueueManagingSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    scopeOpened(
      state,
      action: {
        payload: { accountId: string; enabled: boolean; timeZone: string };
      }
    ) {
      state.accountId = action.payload.accountId;
      state.enabled = action.payload.enabled;
      state.persistedEnabled = action.payload.enabled;
      state.persistedSchedules = {};
      state.schedules = {};
      state.notifyEmptyQueue = false;
      state.persistedNotifyEmptyQueue = false;
      state.bestTimeSlots = [];
      state.timeZone = action.payload.timeZone;
      state.persistedTimeZone = action.payload.timeZone;
      state.showBestTime = true;
    },
    scopeClosed() {
      return initialState;
    },
    queueEnableToggled(state) {
      state.enabled = !state.enabled;
    },
    notifyEmptyQueueToggled(state) {
      state.notifyEmptyQueue = !state.notifyEmptyQueue;
    },
    showBestTimeToggled(state) {
      state.showBestTime = !state.showBestTime;
    },
    draftReset(state) {
      state.enabled = state.persistedEnabled;
      state.schedules = state.persistedSchedules;
      state.notifyEmptyQueue = state.persistedNotifyEmptyQueue;
      state.timeZone = state.persistedTimeZone;
    },
    timeSlotAdded(state, action: { payload: { day: number; value: number } }) {
      const { day, value } = action.payload;

      state.schedules = {
        ...state.schedules,
        [day]: [...(state.schedules[day] ?? []), value].sort((a, b) => a - b),
      };
    },
    timeSlotEdited(
      state,
      action: { payload: { day: number; oldValue: number; value: number } }
    ) {
      const { day, oldValue, value } = action.payload;
      const dayItems = [...(state.schedules[day] ?? [])];
      const index = dayItems.indexOf(oldValue);

      if (index >= 0) {
        dayItems.splice(index, 1, value);
      }

      state.schedules = {
        ...state.schedules,
        [day]: dayItems.sort((a, b) => a - b),
      };
    },
    timeSlotDeleted(
      state,
      action: { payload: { day: number; value: number } }
    ) {
      const { day, value } = action.payload;

      state.schedules = {
        ...state.schedules,
        [day]: (state.schedules[day] ?? []).filter(
          (stateValue) => stateValue !== value
        ),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(openAccountQueueSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(openAccountQueueSettings.fulfilled, (state, action) => {
      const queueSettings = action.payload.settings;
      const parsedSchedules = parseTimeSlots(queueSettings?.schedules ?? []);

      state.loading = false;
      state.persistedSchedules = parsedSchedules;
      state.schedules = parsedSchedules;
      if (
        typeof action.meta.arg === "string" ||
        !action.meta.arg.legacyAutoSave
      ) {
        state.enabled = queueSettings?.enabled ?? state.enabled;
      }
      state.persistedEnabled = state.enabled;
      state.notifyEmptyQueue = queueSettings?.notifyEmptyQueue ?? false;
      state.persistedNotifyEmptyQueue = state.notifyEmptyQueue;
      state.bestTimeSlots = queueSettings?.bttp ?? [];
      state.timeZone = queueSettings?.timezone ?? state.timeZone;
      state.persistedTimeZone = state.timeZone;
    });
    builder.addCase(openAccountQueueSettings.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(saveAccountQueueSettings.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(saveAccountQueueSettings.fulfilled, (state) => {
      state.saving = false;
      state.persistedEnabled = state.enabled;
      state.persistedSchedules = state.schedules;
      state.persistedNotifyEmptyQueue = state.notifyEmptyQueue;
      state.persistedTimeZone = state.timeZone;
    });
    builder.addCase(saveAccountQueueSettings.rejected, (state, action) => {
      state.saving = false;
      if (action.meta.arg?.legacyAutoSave) {
        state.schedules = state.persistedSchedules;
        return;
      }

      // The legacy editor has no global Save retry path, so a failed immediate
      // write must return to the last server snapshot. Coordinated silent saves
      // retain their draft for the footer's retry behavior.
      if (!action.meta.arg?.silent) {
        state.enabled = state.persistedEnabled;
        state.schedules = state.persistedSchedules;
        state.notifyEmptyQueue = state.persistedNotifyEmptyQueue;
        state.timeZone = state.persistedTimeZone;
      }
    });

    builder.addCase(clearAccountQueueFx.pending, (state) => {
      state.clearing = true;
    });
    builder.addCase(clearAccountQueueFx.fulfilled, (state, action) => {
      state.clearing = false;
      state.persistedSchedules = {};
      state.schedules = {};
      if (!action.meta.arg?.legacyAutoSave) {
        state.enabled = false;
        state.persistedEnabled = false;
        state.notifyEmptyQueue = false;
        state.persistedNotifyEmptyQueue = false;
      }
    });
    builder.addCase(clearAccountQueueFx.rejected, (state) => {
      state.clearing = false;
    });
  },
});

export const accountQueueManagingReducer = accountQueueManagingSlice.reducer;
export const accountQueueManagingActions = accountQueueManagingSlice.actions;

export const selectAccountQueueManagingState = createSelector(
  getFeatureState,
  (queueState) => queueState
);

export const selectCurrentQueueAccountId = createSelector(
  selectAccountQueueManagingState,
  (queueState) => queueState.accountId
);

export const selectCurrentQueueSchedules = createSelector(
  selectAccountQueueManagingState,
  (queueState) => queueState.schedules
);

export const selectCurrentQueueSettingsPayload = createSelector(
  selectAccountQueueManagingState,
  (queueState) => ({
    enabled: queueState.enabled,
    notifyEmptyQueue: queueState.notifyEmptyQueue,
    schedules: formatTimeSlots(queueState.schedules),
    timezone: queueState.timeZone,
  })
);

export const selectSelectedQueueAccount = createSelector(
  [(state: ReduxRootState) => state, selectCurrentQueueAccountId],
  (state, accountId) => {
    if (!accountId) {
      return null;
    }

    return selectAccountById(state, accountId);
  }
);

export const selectQueueSlotsCount = createSelector(
  selectCurrentQueueSchedules,
  (schedules) =>
    Object.values(schedules).reduce(
      (sum, slots) => sum + (slots as number[]).length,
      0
    )
);

export const selectAccountQueueUpdating = createSelector(
  selectAccountQueueManagingState,
  (queueState) => queueState.saving || queueState.clearing
);

// An empty day and an omitted day both represent no slots. Keep this
// normalization in the comparison so saving retains the existing API payload.
function comparableSchedules(schedules: QueueSchedules) {
  return Object.entries(schedules).filter(([, slots]) => slots.length > 0);
}

export const selectAccountQueueDirty = createSelector(
  selectAccountQueueManagingState,
  (queueState) =>
    queueState.enabled !== queueState.persistedEnabled ||
    queueState.notifyEmptyQueue !== queueState.persistedNotifyEmptyQueue ||
    queueState.timeZone !== queueState.persistedTimeZone ||
    JSON.stringify(comparableSchedules(queueState.schedules)) !==
      JSON.stringify(comparableSchedules(queueState.persistedSchedules))
);

export const selectTimeSlotsByDays = createSelector(
  selectCurrentQueueSchedules,
  selectAccountQueueManagingState,
  (schedules, queueState) =>
    getTimeSlotsByDays(
      schedules,
      queueState.bestTimeSlots,
      queueState.showBestTime
    )
);

export const selectAccountQueueModel = createSelector(
  selectAccountQueueManagingState,
  selectTimeSlotsByDays,
  selectSelectedQueueAccount,
  selectQueueSlotsCount,
  (queueState, timeSlotsByDays, selectedAccount, slotsCount) => ({
    queueEnabled: queueState.enabled,
    showBestTime: queueState.showBestTime,
    timeSlotsByDays,
    notifyEmptyQueue: queueState.notifyEmptyQueue,
    loading: queueState.loading,
    updating: queueState.saving || queueState.clearing,
    selectedAccount,
    slotsCount,
    timeZone: queueState.timeZone,
  })
);

export function useAccountQueueScope(
  accountId: string,
  options?: { legacyAutoSave?: boolean }
) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      openAccountQueueSettings({
        accountId,
        legacyAutoSave: options?.legacyAutoSave,
      }) as never
    );

    return () => {
      dispatch(accountQueueManagingActions.scopeClosed());
    };
  }, [accountId, dispatch, options?.legacyAutoSave]);
}

export function useAccountQueueModel() {
  return useSelector(selectAccountQueueModel);
}
